import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HighZTooltipProps } from './types';

const HighZTooltip = ({
  children,
  content,
  position = 'top',
  delay = 300,
  className = '',
}: HighZTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    setTimeout(() => setIsVisible(true), delay);
  };

  const hideTooltip = () => {
    setTimeout(() => setIsVisible(false), delay);
  };

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - 35;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + 5;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 5;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 5;
          break;
      }

      setTooltipPosition({ top, left });
    }
  }, [isVisible, position]);

  const getTooltipClasses = () => {
    const baseClasses =
      'px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap pointer-events-none transition-opacity duration-200';

    switch (position) {
      case 'top':
        return `${baseClasses} transform -translate-x-1/2 -translate-y-full ${className}`;
      case 'bottom':
        return `${baseClasses} transform -translate-x-1/2 ${className}`;
      case 'left':
        return `${baseClasses} transform -translate-x-full -translate-y-1/2 ${className}`;
      case 'right':
        return `${baseClasses} transform -translate-y-1/2 ${className}`;
      default:
        return baseClasses;
    }
  };

  const renderTooltip = () => {
    if (!isVisible || !content) return null;

    return ReactDOM.createPortal(
      <div
        className={`fixed ${getTooltipClasses()} ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          zIndex: 999999,
        }}
      >
        {content}
      </div>,
      document.body,
    );
  };

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {renderTooltip()}
    </div>
  );
};

export default HighZTooltip;
